import React, { SyntheticEvent } from "react";
import cx from "classnames";
import { dataTestIds } from "data-testids";
import { Button } from "../../Atoms/Button/Button";

export interface IStickyButtonConfig {
  text: string;
  onClick: (e: SyntheticEvent<Element, Event>) => void;
  disabled?: boolean;
  loading?: boolean;
  id?: string;
  wrapperClassName?: string;
  dataTestId?: string;
  zIndex?: string;
}

interface IStickyCTABarProps {
  next?: IStickyButtonConfig;
  prev?: IStickyButtonConfig;
  cancel?: IStickyButtonConfig;
  nextUpHint?: string;
  i18nTextsForNextUpHint?: Record<string, string>;
  isSticky?: boolean;
  ctaBarAdditionalClassName?: string;
  zIndex?: "z-5";
}

const StickyCTABar: React.FC<React.PropsWithChildren<IStickyCTABarProps>> = ({
  next,
  prev,
  cancel,
  nextUpHint,
  i18nTextsForNextUpHint,
  isSticky = true,
  ctaBarAdditionalClassName,
  zIndex,
}) => {
  const shouldShowNextUpHint =
    nextUpHint && i18nTextsForNextUpHint && Object.keys(i18nTextsForNextUpHint);
  const ctaBarWrapperClassNames = cx(
    "bottom-0 flex justify-center w-full print:hidden",
    { sticky: isSticky },
    zIndex ?? "z-50"
  );
  const ctaBarClassNames = cx(
    "w-full px-3 py-2 rounded-t-lg bg-color-neutral-three max-w-7xl md:py-1",
    ctaBarAdditionalClassName
  );

  return (
    <div id="sticky-bar" className={ctaBarWrapperClassNames}>
      <div
        className={ctaBarClassNames}
        style={{ boxShadow: "0px -6px 10px rgba(0, 0, 0, 0.2)" }}
      >
        <main className="flex w-full items-center">
          {shouldShowNextUpHint && (
            <div className="hidden flex-1 text-lg md:block">
              <span className="font-semibold">
                {i18nTextsForNextUpHint.nextUpLabel}
              </span>
              {nextUpHint}
            </div>
          )}
          <section className="flex w-full flex-1 flex-col justify-around space-y-2 md:flex-row-reverse md:justify-start md:space-y-0">
            {next && (
              <div
                className={cx(
                  "w-fit-content md2:min-w-60 md:ml-2 md:min-w-60",
                  next.wrapperClassName
                )}
              >
                <Button
                  type="primary"
                  size="medium"
                  text={next.text}
                  disabled={next.disabled}
                  onClick={next.onClick}
                  loading={next.loading}
                  id={next.id}
                  data-testid={
                    next?.dataTestId ||
                    dataTestIds.componentLibrary[
                      "Organisms.StickyCTABar.nextButton"
                    ]
                  }
                />
              </div>
            )}
            <div className="flex w-full justify-end space-x-2">
              {prev && (
                <div
                  className={cx(
                    "md2:w-60 w-full md:w-48",
                    prev.wrapperClassName
                  )}
                >
                  <Button
                    type="secondary"
                    size="medium"
                    text={prev.text}
                    onClick={prev.onClick}
                    loading={prev.loading}
                    id={prev.id}
                    data-testid={
                      prev?.dataTestId ||
                      dataTestIds.componentLibrary[
                        "Organisms.StickyCTABar.prevButton"
                      ]
                    }
                  />
                </div>
              )}

              {cancel && (
                <div
                  className={cx(
                    "md2:w-60 w-full md:w-48",
                    cancel.wrapperClassName
                  )}
                >
                  <Button
                    type="secondary"
                    size="medium"
                    text={cancel.text}
                    onClick={cancel.onClick}
                    loading={cancel.loading}
                    id={cancel.id}
                    data-testid={
                      cancel?.dataTestId ||
                      dataTestIds.componentLibrary[
                        "Organisms.StickyCTABar.cancelButton"
                      ]
                    }
                  />
                </div>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default StickyCTABar;
